<script setup lang="ts">
import { ref, computed } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import env from '../config/env'
import { useAuthStore } from '../stores/auth'
import { useToastStore } from '../stores/toast'
import playerImportService from '../services/playerImport.service'
import { detectStartRow, autoMapColumns, saveImportState, type ImportSource } from '../composables/usePlayerImport'

const emit = defineEmits<{ (e: 'close'): void }>()

const router = useRouter()
const authStore = useAuthStore()
const toastStore = useToastStore()
const { t, locale } = useI18n()

const clubUuid = computed(() => authStore.initData?.club?.uuid || '')
const tutorialUrl = computed(() => `${env.web.url}/${locale.value}/sportbm-import-tutorial`)

const source = ref<ImportSource>('sportbm')
const file = ref<File | null>(null)
const loading = ref(false)

const onFileChange = (event: Event) => {
  const input = event.target as HTMLInputElement
  file.value = input.files?.[0] || null
}

const submit = async () => {
  if (!file.value || !clubUuid.value) return

  loading.value = true
  try {
    const { rows } = await playerImportService.parseFile(clubUuid.value, source.value, file.value)
    if (!rows.length) {
      toastStore.error(t('playerImport.emptyFile'))
      return
    }

    const startRow = detectStartRow(rows)
    saveImportState({
      clubUuid: clubUuid.value,
      source: source.value,
      fileName: file.value.name,
      rows,
      startRow,
      mapping: autoMapColumns(rows, startRow),
      reviewRows: null,
    })

    emit('close')
    router.push('/players/import/mapping')
  } catch (err: any) {
    console.error('Parse import file error:', err)
    toastStore.error(err.response?.data?.message || t('playerImport.parseError'))
  } finally {
    loading.value = false
  }
}
</script>

<template>
  <div
    class="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
    @click.self="emit('close')"
  >
    <div class="bg-white rounded-lg shadow-xl max-w-md w-full" @click.stop>
      <div class="p-6">
        <h3 class="text-lg font-semibold text-gray-900 mb-4">{{ t('playerImport.modalTitle') }}</h3>

        <div class="space-y-4">
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">{{ t('playerImport.whichApp') }}</label>
            <select
              v-model="source"
              class="w-full px-3 py-2 text-sm border-2 border-gray-300 rounded focus:outline-none focus:border-blue-600"
            >
              <option value="sportbm">Sport BM</option>
            </select>
            <a
              v-if="source === 'sportbm'"
              :href="tutorialUrl"
              target="_blank"
              rel="noopener"
              class="mt-1 inline-block text-sm text-blue-600 hover:text-blue-800 hover:underline"
            >
              {{ t('playerImport.sportbmTutorial') }}
            </a>
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">{{ t('playerImport.file') }}</label>
            <input
              type="file"
              accept=".xlsx,.xls,.csv"
              class="w-full text-sm text-gray-700 file:mr-3 file:px-3 file:py-2 file:rounded file:border-2 file:border-gray-300 file:bg-gray-100 file:text-gray-700 file:font-medium hover:file:bg-gray-200"
              @change="onFileChange"
            >
            <p class="mt-1 text-xs text-gray-500">{{ t('playerImport.fileHint') }}</p>
          </div>
        </div>

        <div class="mt-6 flex justify-end gap-2">
          <button
            type="button"
            class="px-4 py-2 bg-gray-100 text-gray-700 text-sm font-medium rounded hover:bg-gray-200 border-2 border-gray-300"
            @click="emit('close')"
          >
            {{ t('common.cancel') }}
          </button>
          <button
            type="button"
            :disabled="!file || loading"
            class="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded hover:bg-blue-700 border-2 border-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
            @click="submit"
          >
            {{ loading ? t('playerImport.reading') : t('playerImport.showList') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
